import React from 'react';
import {
  Document,
  PDFViewer,
  Page as PDFPage,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage
} from '@react-pdf/renderer';
import { formatDate } from '../../../../../utils/common';

const styles = {
  page: {
    padding: 20
  },
  heading: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20
  },
  contentContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0,
    border: '1px solid black',
    padding: '10px'
  },
  row: {
    flexDirection: 'row',
    marginBottom: 0
  },
  text: {
    fontSize: 9,
    display: 'inline-block'
  },
  columnHeader: {
    width: '100%',
    backgroundColor: '#1976D2',
    color: 'white',
    padding: 5,
    textAlign: 'center',
    fontSize: 8,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word'
  },
  columnContent: {
    width: '100%',
    padding: 5,
    textAlign: 'center',
    fontSize: 7.5,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word'
  }
};

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

const groupBySponsor = (data) => {
  const grouped = data.reduce((acc, item) => {
    const sponsorName = item.sponsor?.name || 'Unknown Sponsor';
    if (!acc[sponsorName]) {
      acc[sponsorName] = {
        patients: [],
        totalNetAmount: 0
      };
    }
    acc[sponsorName].patients.push(item);
    acc[sponsorName].totalNetAmount += Number(item.ipd?.total || 0);
    return acc;
  }, {});

  Object.keys(grouped).forEach(sponsor => {
    grouped[sponsor].patients.sort((a, b) => {
      const ipdA = a?.ipd?.ipd_no || '';
      const ipdB = b?.ipd?.ipd_no || '';
      return ipdA.localeCompare(ipdB);
    });
  });

  return grouped;
};

const safeFormatDate = (date) => {
  if (!date) return '';
  try {
    return formatDate(date);
  } catch {
    return '';
  }
};

const getAgeFromDOB = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  return age;
};

const COLORS = ['#f0f0f0', '#e0e0e0'];

export const SponsorWiseIpdReportPdf = ({ data }) => {
  const groupedData = groupBySponsor(data);

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Sponsor Wise IPD Report">
          <Page style={styles.page} size="A4" orientation="landscape">
            <PDFImage
              src="/logo_Hospital_m.jpeg"
              style={{ width: '100%', height: '80px', marginBottom: 1, marginRight: '10%' }}
            />
            {Object.keys(groupedData).map((sponsorName) => (
              <View key={sponsorName}>
                <View style={{ ...styles.row, width: '100%' }}>
                  <Text style={{ ...styles.columnHeader, width: '50%' }} content="Sr." />
                  <Text style={styles.columnHeader} content="UHID" />
                  <Text style={styles.columnHeader} content="IPD No." />
                  <Text style={styles.columnHeader} content="Adm Date" />
                  <Text style={styles.columnHeader} content="Adm Time" />
                  <Text style={styles.columnHeader} content="Dis. Date" />
                  <Text style={styles.columnHeader} content="Dis. Time" />
                  <Text style={styles.columnHeader} content="Sponsor" />
                  <Text style={styles.columnHeader} content="Patient Name" />
                  <Text style={{ ...styles.columnHeader, width: '50%' }} content="Age" />
                  <Text style={styles.columnHeader} content="Gender" />
                  <Text style={styles.columnHeader} content="Add" />
                  <Text style={styles.columnHeader} content="Admission Type" />
                  <Text style={styles.columnHeader} content="Doctor Name" />
                  <Text style={styles.columnHeader} content="CareTaker-Name" />
                  <Text style={styles.columnHeader} content="CareTaker-Relation" />
                  <Text style={styles.columnHeader} content="CareTaker-Mobile" />
                </View>
                {groupedData[sponsorName].patients.map((item, index) => (
                  <View key={index} style={{ ...styles.row, backgroundColor: COLORS[index % COLORS.length] }}>
                    <Text style={{ ...styles.columnContent, width: '50%' }} content={index + 1} />
                    <Text style={styles.columnContent} content={item?.patients?.uhid || ''} />
                    <Text style={styles.columnContent} content={item?.ipd_no || ''} />
                    <Text style={styles.columnContent} content={safeFormatDate(item?.date)} />
                    <Text style={styles.columnContent} content={item?.time || ''} />
                    <Text style={styles.columnContent} content={safeFormatDate(item?.patientDischartDetails?.date)} />
                    <Text style={styles.columnContent} content={item?.patientDischartDetails?.time || ''} />
                    <Text style={styles.columnContent} content={item?.sponsor?.name || ''} />
                    <Text
                      style={{ ...styles.columnContent, textAlign: 'left' }}
                      content={`${item?.patients?.name_suffix || ''} ${item?.patients?.first_name || ''} ${item?.patients?.last_name || ''}`}
                    />
                    <Text style={{ ...styles.columnContent, width: '50%' }} content={getAgeFromDOB(item?.patients?.dob)} />
                    <Text style={styles.columnContent} content={item?.patients?.gender || ''} />
                    <Text style={styles.columnContent} content={item?.patients?.address?.address || ''} />
                    <Text style={{ ...styles.columnContent, textAlign: 'left' }} content={item?.admission_type || ''} />
                    <Text style={{ ...styles.columnContent, textAlign: 'left' }} content={item?.doctor?.name || ''} />
                    <Text
                      style={{ ...styles.columnContent, textAlign: 'left' }}
                      content={item?.care_taker?.name_1 || item?.ipd?.care_taker?.name_2 || ''}
                    />
                    <Text
                      style={{ ...styles.columnContent, textAlign: 'left' }}
                      content={item?.care_taker?.relation_1 || item?.care_taker?.relation_2 || ''}
                    />
                    <Text
                      style={{ ...styles.columnContent, textAlign: 'left' }}
                      content={item?.care_taker?.mobile_1 || item?.care_taker?.mobile_2 || ''}
                    />
                  </View>
                ))}
              </View>
            ))}
          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default SponsorWiseIpdReportPdf;
